import { createAsyncThunk, createSelector,createSlice } from '@reduxjs/toolkit'
import { RootState } from 'store'

import AiPricingModelsRepository from 'services/repositories/AiPricingModelsRepository'
import AiUsageLogsRepository from 'services/repositories/AiUsageLogsRepository'
import {
  AIPricingModelDto,
  AIUsageLogsParams,
  AIUsageLogsResponse,
  AIUsageStatisticsResponse,
  CreatePricingModelPayload,
  SetPricingModelActivePayload,
  UpdatePricingModelPayload} from 'types/aiAnalytics'
import { TLoadingStatus } from 'types/user'

interface IAiAnalyticsState {
  usageLogs: AIUsageLogsResponse | null
  statistics: AIUsageStatisticsResponse | null
  pricingModels: AIPricingModelDto[]
  status: TLoadingStatus
  statisticsStatus: TLoadingStatus
  pricingModelsStatus: TLoadingStatus
  error: string | null
}

const initialState: IAiAnalyticsState = {
  usageLogs: null,
  statistics: null,
  pricingModels: [],
  status: 'initial',
  statisticsStatus: 'initial',
  pricingModelsStatus: 'initial',
  error: null
}

export const types = {
  getUsageLogs: 'aiAnalytics/getUsageLogs',
  getStatistics: 'aiAnalytics/getStatistics',
  getPricingModels: 'aiAnalytics/getPricingModels',
  createPricingModel: 'aiAnalytics/createPricingModel',
  updatePricingModel: 'aiAnalytics/updatePricingModel',
  deletePricingModel: 'aiAnalytics/deletePricingModel',
  setPricingModelActive: 'aiAnalytics/setPricingModelActive'
} as const

// Usage Logs Actions
export const getUsageLogs = createAsyncThunk(
  types.getUsageLogs,
  async (params: AIUsageLogsParams, { rejectWithValue }) => {
    try {
      const response = await AiUsageLogsRepository.getUsageLogs(params)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response?.data || 'Failed to fetch usage logs')
    }
  }
)

export const getStatistics = createAsyncThunk(
  types.getStatistics,
  async (params: Partial<AIUsageLogsParams>, { rejectWithValue }) => {
    try {
      const response = await AiUsageLogsRepository.getStatistics(params)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response?.data || 'Failed to fetch statistics')
    }
  }
)

// Pricing Models Actions
export const getPricingModels = createAsyncThunk(
  types.getPricingModels,
  async (activeOnly: boolean = false, { rejectWithValue }) => {
    try {
      const response = await AiPricingModelsRepository.getAllPricingModels(
        activeOnly
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || 'Failed to fetch pricing models'
      )
    }
  }
)

export const createPricingModel = createAsyncThunk(
  types.createPricingModel,
  async (payload: CreatePricingModelPayload, { rejectWithValue }) => {
    try {
      const response = await AiPricingModelsRepository.createPricingModel(
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || 'Failed to create pricing model'
      )
    }
  }
)

export const updatePricingModel = createAsyncThunk(
  types.updatePricingModel,
  async (
    { id, payload }: { id: string; payload: UpdatePricingModelPayload },
    { rejectWithValue }
  ) => {
    try {
      const response = await AiPricingModelsRepository.updatePricingModel(
        id,
        payload
      )

      return response.data
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || 'Failed to update pricing model'
      )
    }
  }
)

export const deletePricingModel = createAsyncThunk(
  types.deletePricingModel,
  async (id: string, { rejectWithValue }) => {
    try {
      await AiPricingModelsRepository.deletePricingModel(id)

      return id
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || 'Failed to delete pricing model'
      )
    }
  }
)

export const setPricingModelActive = createAsyncThunk(
  types.setPricingModelActive,
  async (
    { id, payload }: { id: string; payload: SetPricingModelActivePayload },
    { rejectWithValue }
  ) => {
    try {
      const response = await AiPricingModelsRepository.setActive(id, payload)

      return response.data
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || 'Failed to update pricing model status'
      )
    }
  }
)

export const aiAnalyticsSlice = createSlice({
  name: 'aiAnalytics',
  initialState,
  reducers: {
    clearUsageLogs: state => {
      state.usageLogs = null
      state.error = null
    },
    clearStatistics: state => {
      state.statistics = null
      state.error = null
    }
  },
  extraReducers: builder => {
    builder
      // Usage Logs
      .addCase(getUsageLogs.pending, state => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(getUsageLogs.fulfilled, (state, action) => {
        state.status = 'loaded'
        state.usageLogs = action.payload
        state.error = null
      })
      .addCase(getUsageLogs.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload as string
      })
      // Statistics
      .addCase(getStatistics.pending, state => {
        state.statisticsStatus = 'loading'
        state.error = null
      })
      .addCase(getStatistics.fulfilled, (state, action) => {
        state.statisticsStatus = 'loaded'
        state.statistics = action.payload
        state.error = null
      })
      .addCase(getStatistics.rejected, (state, action) => {
        state.statisticsStatus = 'failed'
        state.error = action.payload as string
      })
      // Pricing Models
      .addCase(getPricingModels.pending, state => {
        state.pricingModelsStatus = 'loading'
        state.error = null
      })
      .addCase(getPricingModels.fulfilled, (state, action) => {
        state.pricingModelsStatus = 'loaded'
        state.pricingModels = action.payload
        state.error = null
      })
      .addCase(getPricingModels.rejected, (state, action) => {
        state.pricingModelsStatus = 'failed'
        state.error = action.payload as string
      })
      .addCase(createPricingModel.fulfilled, (state, action) => {
        state.pricingModels.push(action.payload)
      })
      .addCase(updatePricingModel.fulfilled, (state, action) => {
        const index = state.pricingModels.findIndex(
          model => model.id === action.payload.id
        )

        if (index !== -1) {
          state.pricingModels[index] = action.payload
        }
      })
      .addCase(deletePricingModel.fulfilled, (state, action) => {
        state.pricingModels = state.pricingModels.filter(
          model => model.id !== action.payload
        )
      })
      .addCase(setPricingModelActive.fulfilled, (state, action) => {
        const index = state.pricingModels.findIndex(
          model => model.id === action.payload.id
        )

        if (index !== -1) {
          state.pricingModels[index] = action.payload
        }
      })
  }
})

export const { clearUsageLogs, clearStatistics } = aiAnalyticsSlice.actions

// Selectors
export const selectAiAnalytics = (state: RootState) => state.aiAnalytics

export const selectUsageLogs = createSelector(
  selectAiAnalytics,
  aiAnalytics => aiAnalytics.usageLogs
)

export const selectStatistics = createSelector(
  selectAiAnalytics,
  aiAnalytics => aiAnalytics.statistics
)

export const selectPricingModels = createSelector(
  selectAiAnalytics,
  aiAnalytics => aiAnalytics.pricingModels
)

export const selectActivePricingModels = createSelector(
  selectPricingModels,
  pricingModels => pricingModels.filter(model => model.isActive)
)

export const selectAiAnalyticsStatus = createSelector(
  selectAiAnalytics,
  aiAnalytics => aiAnalytics.status
)

export const selectStatisticsStatus = createSelector(
  selectAiAnalytics,
  aiAnalytics => aiAnalytics.statisticsStatus
)

export const selectPricingModelsStatus = createSelector(
  selectAiAnalytics,
  aiAnalytics => aiAnalytics.pricingModelsStatus
)

export const selectAiAnalyticsError = createSelector(
  selectAiAnalytics,
  aiAnalytics => aiAnalytics.error
)

export default aiAnalyticsSlice.reducer
